import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

// ============================================
// 권한 없음 페이지
// ============================================
const NoPermissionContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: 'calc(100vh - 120px)',
  gap: '16px',
  color: '#999',
});

const LockIcon = styled(LockOutlinedIcon)({
  fontSize: '64px',
  color: '#ccc',
});

const Title = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#555',
});

const Description = styled(Typography)({
  fontSize: '14px',
  color: '#999',
  textAlign: 'center',
  lineHeight: 1.8,
});

const GuideBox = styled(Box)({
  marginTop: '16px',
  padding: '20px 32px',
  backgroundColor: '#f9f9f9',
  borderRadius: '8px',
  border: '1px solid #eee',
  maxWidth: '400px',
});

const GuideTitle = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
  marginBottom: '8px',
});

const GuideStep = styled(Typography)({
  fontSize: '13px',
  color: '#888',
  lineHeight: 2,
});

const NoPermissionPage = () => (
  <NoPermissionContainer>
    <LockIcon />
    <Title>접근 권한이 없습니다</Title>
    <Description>
      이 페이지를 이용하려면 관리자에게 권한을 요청하세요.
    </Description>
    <GuideBox>
      <GuideTitle>권한 설정 방법</GuideTitle>
      <GuideStep>1. 센터 관리 &gt; 권한관리 메뉴로 이동</GuideStep>
      <GuideStep>2. 좌측에서 직원을 선택</GuideStep>
      <GuideStep>3. 필요한 권한 체크 후 저장</GuideStep>
    </GuideBox>
  </NoPermissionContainer>
);

// ============================================
// 상수
// ============================================

// ROLE_SUPERADMIN 전용 경로
const superAdminOnlyPaths = ['/facility-control', '/facility-control/face-access-group'];

// 매니저 예외 없이 SUPERADMIN에게만 허용하는 경로 (시설제어처럼 매니저 예외를 두지 않는다)
const superAdminExclusivePaths = ['/app-version'];

// 백엔드 메뉴 등록 없이 역할 기반으로 강제 허용할 경로
const roleForceAllowPaths = ['/unconfirmed-attendance'];
const roleForceAllowRoles = ['ROLE_SUPERADMIN', 'ROLE_OPERATOR', 'ROLE_MANAGER'];

// 경로 → PAGE_VIEW 권한 코드 매핑 (대메뉴)
const PATH_PAGE_VIEW_MAP = {
  '/attendHistory': 'ATTEND_PAGE_VIEW',
  '/salesManagement': 'SALES_PAGE_VIEW',
};

// 경로 → 소메뉴 권한 코드 매핑
const PATH_SUBMENU_AUTH_MAP = {
  '/members': 'MEMBER_UNREGISTERED_VIEW',
  '/member': 'MEMBER_GENERAL_VIEW',
  '/corp': 'MEMBER_CORP_VIEW',
  '/temporary': 'MEMBER_TEMP_VIEW',
  '/member-app-link': 'MEMBER_GENERAL_VIEW',
  '/private': 'SCHEDULE_PRIVATE_VIEW',
  '/group': 'SCHEDULE_GROUP_VIEW',
  '/online': 'SCHEDULE_GOLF_MANAGE',
  '/ticket': 'TICKET_COURSE_VIEW',
  '/pass': 'TICKET_PASS_VIEW',
  '/trial': 'TICKET_TRIAL_VIEW',
  '/chat': 'COMMUNITY_CHAT_VIEW',
  '/notice': 'COMMUNITY_NOTICE_VIEW',
  '/event': 'COMMUNITY_EVENT_VIEW',
  '/banner': 'COMMUNITY_BANNER_VIEW',
  '/transfer': 'COMMUNITY_TRANSFER_VIEW',
  '/sms': 'COMMUNITY_SMS_VIEW',
  '/employee': 'EMPLOYEE_SETTING_VIEW',
  '/salary': 'EMPLOYEE_SALARY_VIEW',
  '/salarySettlement': 'EMPLOYEE_SETTLEMENT_VIEW',
  '/salarySetting': 'EMPLOYEE_SALARY_SETTING_VIEW',
  '/attendanceManagement': 'EMPLOYEE_ATTENDANCE_MGMT_VIEW',
  '/store': 'CENTER_STORE_VIEW',
  '/product': 'CENTER_PRODUCT_VIEW',
  '/locker': 'CENTER_LOCKER_VIEW',
  '/point-management': 'CENTER_POINT_VIEW',
  '/permission': 'CENTER_PERMISSION_VIEW',
};

// ============================================
// Wrapper 컴포넌트
// ============================================

// 권한 코드 체크 헬퍼 (훅 밖에서 사용)
const checkAuth = (userRole, menus, code) => {
  if (!code) return false;
  if (userRole === 'ROLE_SUPERADMIN') return true;
  return menus?.some(m => m.menuAuthCode === code) || false;
};

export function MainMenuWrapper({ children, path }) {
  const userRole = useSelector(state => state.user.user.userRole);
  // 시설 제어 노출 — 기업회원(center) 단위 설정값. 로그인 응답으로 내려온다.
  const facilityControlYn = useSelector(state => state.user.user.facilityControlYn);
  // 골프 관리 노출 — 기업회원(center) 단위 설정값. 메뉴 9(/online)는 매니저에게 자동 부여되므로 여기서 막아야 URL 직접 진입이 차단된다.
  const golfYn = useSelector(state => state.user.user.golfYn);
  const allMenus = useSelector(state => state.menu.menu);
  const menus = useMemo(() => allMenus?.filter(menu => menu.menuAuthCode === 'SELECT') || [], [allMenus]);

  const submenuAuthCode = PATH_SUBMENU_AUTH_MAP[path?.trim()];
  const pageViewCode = PATH_PAGE_VIEW_MAP[path?.trim()];

  // 골프 타석 관리 — 기업회원 골프 관리 설정이 꺼진 매니저는 SCHEDULE_GOLF_MANAGE 권한이 있어도 차단
  if (path?.trim() === '/online' && userRole !== 'ROLE_SUPERADMIN' && !(userRole === 'ROLE_MANAGER' && golfYn === 'Y')) {
    return <NoPermissionPage />;
  }

  // 소메뉴 권한 체크
  if (submenuAuthCode) {
    return checkAuth(userRole, allMenus, submenuAuthCode) ? children : <NoPermissionPage />;
  }

  // PAGE_VIEW 권한 체크 (소메뉴 없는 단일 페이지: 입장내역, 매출)
  if (pageViewCode) {
    return checkAuth(userRole, allMenus, pageViewCode) ? children : <NoPermissionPage />;
  }

  // ROLE_SUPERADMIN 전용 경로 체크 (매니저 예외 없음)
  if (superAdminExclusivePaths.includes(path?.trim())) {
    return userRole === 'ROLE_SUPERADMIN' ? children : <NoPermissionPage />;
  }

  // ROLE_SUPERADMIN 전용 경로 체크
  if (superAdminOnlyPaths.includes(path?.trim())) {
    if (userRole === 'ROLE_SUPERADMIN') {
      return children;
    }
    // 시설 제어·출입문 관리(안면 인식 그룹) 모두 기업회원 시설제어 설정이 켜진 지점 매니저에게 허용 (#91)
    if (userRole === 'ROLE_MANAGER' && facilityControlYn === 'Y') {
      return children;
    }
    return <NoPermissionPage />;
  }

  if (roleForceAllowPaths.includes(path?.trim()) && roleForceAllowRoles.includes(userRole)) {
    return children;
  }

  if (menus.some(i => i.path?.trim() === path?.trim())) {
    return children;
  }
  return <NoPermissionPage />;
}

export function SubMenuWrapper({ children, path, access }) {
  const userRole = useSelector(state => state.user.user.userRole);
  const allMenus = useSelector(state => state.menu.menu);
  const menus = useMemo(() => allMenus?.filter(menu => menu.menuAuthCode === 'SELECT') || [], [allMenus]);

  const newPath = path?.trim().split('/').slice(0, -1).join('/');

  const submenuAuthCode = PATH_SUBMENU_AUTH_MAP[newPath];
  const pageViewCode = PATH_PAGE_VIEW_MAP[newPath];

  // 소메뉴 권한 체크 (상위 경로 기준)
  if (submenuAuthCode) {
    return checkAuth(userRole, allMenus, submenuAuthCode) ? children : <NoPermissionPage />;
  }

  // PAGE_VIEW 권한 체크 (상위 경로 기준)
  if (pageViewCode) {
    return checkAuth(userRole, allMenus, pageViewCode) ? children : <NoPermissionPage />;
  }

  const menuFound = menus.filter(i => i.path?.trim() === newPath);
  if (menuFound.length > 0) {
    const permission = menuFound[0].accessType;
    const permissionCode = menuFound[0].menuAuthCode;
    if (permission === 'WRITE') {
      return children;
    } else if (permissionCode === 'SELECT') {
      return children;
    } else {
      if (access === permission) {
        return children;
      }
    }
  }
  return <NoPermissionPage />;
}
